import { useState } from 'react';
import { motion } from 'framer-motion';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    problemType: '',
    problemUrgency: '',
    problemDescription: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6ndq2p4a3uojp5fh4ng', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', problemType: '', problemUrgency: '', problemDescription: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-black min-h-screen">
      <Navigation />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative min-h-[60vh] flex items-center bg-black overflow-hidden">
          {/* Background Image */}
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/d4f1ba7a64f65bab3167ebe62e92012c.jpg"
              alt="Mountain sunset landscape"
              width={1920}
              height={1080}
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-black via-transparent to-black/50"></div>
          </div>

          {/* Floating Geometric Elements */}
          <motion.div
            animate={{ y: [0, -20, 0], rotate: [0, 5, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute top-32 right-20 w-32 h-32 border border-gold/30 rounded-full hidden lg:block"
          />
          <motion.div
            animate={{ y: [0, 20, 0], rotate: [0, -5, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute bottom-20 right-40 w-20 h-20 border border-cream/20 hidden lg:block"
          />

          {/* Content */}
          <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-32 w-full">
            <div className="max-w-5xl">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="flex items-center gap-3 mb-8"
              >
                <div className="w-12 h-[2px] bg-gold"></div>
                <span className="text-gold text-sm font-semibold tracking-widest uppercase">
                  Problem Intake
                </span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-6xl md:text-7xl lg:text-8xl font-black text-white leading-[0.95] tracking-tight mb-8"
              >
                <span className="block">Share Your</span>
                <motion.span
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.6 }}
                  className="block text-gold"
                >
                  Problem
                </motion.span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
                className="text-xl md:text-2xl text-cream/80 max-w-2xl leading-relaxed"
              >
                Every problem deserves to be properly identified, documented, and escalated. 
                Begin your journey toward comprehensive problem awareness by completing our intake form.
              </motion.p>
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section className="py-24">
          <div className="w-full grid lg:grid-cols-2">
            {/* Contact Form */}
            <div className="relative bg-black flex items-center justify-center px-6 lg:px-12 py-20 overflow-hidden">
              {/* Background Image */}
              <div className="absolute inset-0">
                <img
                  src="https://readdy.ai/api/search-image?query=Dramatic%20sunset%20over%20rolling%20hills%20and%20valleys%20with%20golden%20light%20streaming%20through%20clouds%2C%20epic%20landscape%20with%20layers%20of%20mountains%20fading%20into%20distance%2C%20warm%20amber%20and%20deep%20orange%20tones%2C%20professional%20nature%20photography%20showcasing%20vast%20wilderness%20panorama%2C%20inspiring%20scenic%20vista&width=800&height=1000&seq=contact-form-sunset-bg&orientation=portrait"
                  alt="Sunset landscape"
                  width={800}
                  height={1000}
                  loading="lazy"
                  className="w-full h-full object-cover object-center"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/90 to-black/85"></div>
                <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/70"></div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="relative z-10 w-full max-w-lg"
              >
                <h2 className="text-4xl font-bold text-white mb-4">
                  Problem Intake Form
                </h2>
                <p className="text-lg text-light-gray mb-12">
                  Provide detailed information about your problem. Our team will review your submission 
                  and contact you within 24-48 hours to begin the identification process.
                </p>

                <form id="problem-intake-form" data-readdy-form onSubmit={handleSubmit} className="space-y-8">
                  <div>
                    <label htmlFor="name" className="block text-sm text-gold font-semibold uppercase tracking-wider mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full bg-transparent border-b border-gold text-white text-lg py-3 focus:outline-none focus:border-white transition-colors placeholder-light-gray"
                      placeholder="John Smith"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm text-gold font-semibold uppercase tracking-wider mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full bg-transparent border-b border-gold text-white text-lg py-3 focus:outline-none focus:border-white transition-colors placeholder-light-gray"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm text-gold font-semibold uppercase tracking-wider mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full bg-transparent border-b border-gold text-white text-lg py-3 focus:outline-none focus:border-white transition-colors placeholder-light-gray"
                      placeholder="(512) 555-0123"
                    />
                  </div>

                  <div>
                    <label htmlFor="problemType" className="block text-sm text-gold font-semibold uppercase tracking-wider mb-2">
                      Problem Category
                    </label>
                    <select
                      id="problemType"
                      name="problemType"
                      value={formData.problemType}
                      onChange={handleChange}
                      required
                      className="w-full bg-transparent border-b border-gold text-white text-lg py-3 focus:outline-none focus:border-white transition-colors cursor-pointer"
                    >
                      <option value="" className="bg-black">Select a category</option>
                      <option value="operational" className="bg-black">Operational Problem</option>
                      <option value="strategic" className="bg-black">Strategic Problem</option>
                      <option value="interpersonal" className="bg-black">Interpersonal Problem</option>
                      <option value="environmental" className="bg-black">Environmental Problem</option>
                      <option value="existential" className="bg-black">Existential Problem</option>
                      <option value="other" className="bg-black">Other Problem</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="problemUrgency" className="block text-sm text-gold font-semibold uppercase tracking-wider mb-2">
                      Problem Urgency Level
                    </label>
                    <select
                      id="problemUrgency"
                      name="problemUrgency"
                      value={formData.problemUrgency}
                      onChange={handleChange}
                      required
                      className="w-full bg-transparent border-b border-gold text-white text-lg py-3 focus:outline-none focus:border-white transition-colors cursor-pointer"
                    >
                      <option value="" className="bg-black">Select urgency level</option>
                      <option value="critical" className="bg-black">Critical - Requires Immediate Identification</option>
                      <option value="high" className="bg-black">High - Needs Prompt Documentation</option>
                      <option value="medium" className="bg-black">Medium - Standard Processing</option>
                      <option value="low" className="bg-black">Low - Routine Awareness</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="problemDescription" className="block text-sm text-gold font-semibold uppercase tracking-wider mb-2">
                      Detailed Problem Description
                    </label>
                    <textarea
                      id="problemDescription"
                      name="problemDescription"
                      value={formData.problemDescription}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full bg-transparent border-b border-gold text-white text-lg py-3 focus:outline-none focus:border-white transition-colors placeholder-light-gray resize-none"
                      placeholder="Please describe the nature of your problem in detail. Include any relevant context, timeline, and impact..."
                    />
                    <div className="text-right text-sm text-light-gray mt-2">
                      {formData.problemDescription.length}/500
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gold text-black py-5 rounded-full text-lg font-bold hover:bg-white transition-all flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {isSubmitting ? 'Submitting Problem...' : 'Submit Problem Intake'}
                    <i className="ri-arrow-right-line text-xl"></i>
                  </button>

                  {submitStatus === 'success' && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-gold/20 border border-gold text-gold px-6 py-4 rounded-lg text-center"
                    >
                      Your problem has been received and will be thoroughly documented. 
                      A Problem Awareness Specialist will contact you shortly.
                    </motion.div>
                  )}

                  {submitStatus === 'error' && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-red-500/20 border border-red-500 text-red-500 px-6 py-4 rounded-lg text-center"
                    >
                      We encountered a problem submitting your problem. Please try again.
                    </motion.div>
                  )}
                </form>

                <div className="mt-16 space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-map-pin-line text-2xl text-gold"></i>
                    </div>
                    <div className="text-white text-lg">Austin, Texas</div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-phone-line text-2xl text-gold"></i>
                    </div>
                    <a href="tel:+15125550123" className="text-white text-lg hover:text-gold transition-colors">
                      (512) 555-0123
                    </a>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-mail-line text-2xl text-gold"></i>
                    </div>
                    <a href="mailto:problems@therealproblems.com" className="text-white text-lg hover:text-gold transition-colors">
                      problems@therealproblems.com
                    </a>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Map */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative h-[800px] lg:h-auto"
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d221094.38763427597!2d-97.94680849999999!3d30.307182!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8644b599a0cc032f%3A0x5d9b464bd469d57a!2sAustin%2C%20TX!5e0!3m2!1sen!2sus!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0, filter: 'grayscale(100%) invert(92%) contrast(83%)' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Austin, Texas location map"
              ></iframe>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Contact;